import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import Modal from '../Modal';
import Alert from '../Alert';
import MergeContacts from './MergeContacts';
import { contactsApi } from '../../requests/api';

// A skipped rule is not in the server's list of available ones, so its label
// cannot come from there — without this the popup named them "name_company".
const RULE_LABELS = {
    email: 'Email',
    cell: 'Cell',
    name_city: 'Name + City',
    name_company: 'Name + Company',
    name: 'Name',
    company_city: 'Company + City',
};

const STRENGTHS = { email: 'definite', cell: 'definite', name_city: 'definite', name_company: 'definite', name: 'possible', company_city: 'possible' };

/**
 * Which rules each entity can run, strongest first — mirrors
 * ContactDuplicateEntities::ENTITIES on the server.
 *
 * Held here as well so the choice is on screen the moment the popup opens:
 * waiting for the first answer to draw the radios meant picking what to match
 * on only after having already searched. The server's list still wins once it
 * replies, so a change there corrects this rather than fighting it.
 */
const ENTITY_RULES = {
    investors: ['email', 'cell', 'name_city', 'name_company', 'name', 'company_city'],
    agents: ['email', 'cell', 'name_company', 'name'],
    firms: ['cell', 'name_city', 'name', 'company_city'],
    vendors: ['email', 'name_city', 'name_company', 'name', 'company_city'],
};

// Every rule, in priority order, so the ones this entity cannot run can be
// shown disabled rather than silently missing.
const ALL_RULES = ['email', 'cell', 'name_city', 'name_company', 'name', 'company_city'];

function defaultRules(entity) {
    return (ENTITY_RULES[entity] || []).map((id) => ({
        id,
        label: RULE_LABELS[id] || id,
        strength: STRENGTHS[id],
    }));
}

const RULE_HINTS = {
    email: 'Exact match once trimmed and lowercased. Records with no email never match here.',
    cell: 'Exact match on the digits alone; punctuation and a leading country code are ignored.',
    name_city: 'Same name in the same city, ignoring case, punctuation and spacing.',
    name_company: 'Same name at the same company, ignoring case, punctuation and spacing.',
    name: 'Same name only — people and companies share names, so these are possible duplicates.',
    company_city: 'Same company in the same city — a weaker signal, so these are possible duplicates.',
};

function plural(count, singular, pluralForm) {
    return `${count.toLocaleString()} ${count === 1 ? singular : pluralForm || singular + 's'}`;
}

export default function FindDuplicateContacts({
    open, entity, label, plural: pluralLabel, route, fetchFilteredSet, filteredCount, onClose,
}) {
    // One rule at a time: picking Email searches on the email alone, picking
    // Name + City on those two alone. Starts on the entity's strongest rule so
    // the choice is visible and changeable before the first search.
    const [rule, setRule] = React.useState(null);
    const [search, setSearch] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [result, setResult] = React.useState(null);
    const [merging, setMerging] = React.useState(null);
    const [notice, setNotice] = React.useState(null);
    // Ids resolved on this screen, so a group the user has dealt with drops
    // out without re-running the whole comparison.
    const [resolvedIds, setResolvedIds] = React.useState([]);
    const [deletingId, setDeletingId] = React.useState(null);
    // Which search the current result answers, so a stale response cannot
    // overwrite a newer one.
    const requestRef = React.useRef(0);
    // The filtered list's ids, fetched once per opening of the popup.
    // `undefined` means not fetched yet; `null` means the list is unfiltered
    // and the server scans the table itself.
    const filteredIdsRef = React.useRef(undefined);

    const lowerPlural = pluralLabel.toLowerCase();

    const run = (chosenRule, chosenSearch, page) => {
        const requestId = ++requestRef.current;
        setBusy(true);
        setError(null);

        // Only the records the list's filter matches are compared, so a
        // filtered list is searched on its own rather than against the whole
        // table. The ids are fetched once and reused for paging, so stepping
        // through pages does not re-run the list query. An unfiltered list
        // resolves to null and the server scans the table itself.
        const idsReady = filteredIdsRef.current !== undefined
            ? Promise.resolve(filteredIdsRef.current)
            : fetchFilteredSet().then((found) => {
                filteredIdsRef.current = found;
                return found;
            });

        idsReady
            .then((recordIds) => {
                if (requestId !== requestRef.current) return null;

                if (recordIds !== null && !recordIds.length) {
                    throw new Error(`This list has no ${lowerPlural} to compare.`);
                }

                return contactsApi.findDuplicates(
                    entity,
                    chosenRule ? [chosenRule] : null,
                    chosenSearch,
                    page,
                    recordIds,
                );
            })
            .then((data) => {
                if (requestId !== requestRef.current || data === null) return;
                setBusy(false);
                setResult(data);
                setResolvedIds([]);

                if (chosenRule === null && data.rules.length) {
                    setRule(data.rules[0].id);
                }
            })
            .catch((err) => {
                if (requestId !== requestRef.current) return;
                setBusy(false);
                setError(err.message || `Could not look for duplicate ${lowerPlural}.`);
            });
    };

    // A fresh popup every time it opens. The search itself waits for the
    // button: comparing a hundred thousand records is not something to start
    // because somebody opened the window.
    React.useEffect(() => {
        if (!open) {
            requestRef.current++;
            return;
        }
        // The strongest rule this entity can run, selected up front so the
        // radios are on screen and changeable before anything is searched.
        const rules = defaultRules(entity);
        setRule(rules.length ? rules[0].id : null);
        setSearch('');
        setError(null);
        setResult(null);
        setNotice(null);
        setMerging(null);
        setResolvedIds([]);
        // The filter may have changed since last time.
        filteredIdsRef.current = undefined;
    }, [open, entity]);

    if (!open) return null;

    // The server's list once it has answered; until then the entity's own,
    // so the rules are pickable from the moment the popup opens.
    const availableRules = result ? result.rules : defaultRules(entity);

    const submit = (event) => {
        if (event) event.preventDefault();
        run(rule, search, 1);
    };

    const goToPage = (page) => run(rule, search, page);

    // Picking a rule re-runs the search straight away: the rule IS the
    // search, so making the user press the button again says nothing new.
    const chooseRule = (id) => {
        setRule(id);
        if (result) run(id, search, 1);
    };

    const resolve = (ids) => setResolvedIds(resolvedIds.concat(ids.map(Number)));

    const onMerged = (data) => {
        setMerging(null);
        resolve(data.merged_ids);
        const moved = Object.keys(data.rows_moved || {}).reduce((sum, table) => sum + data.rows_moved[table], 0);
        setNotice(
            `Merged ${plural(data.merged_ids.length, 'record')} into ${data.primary_id}.`
            + (data.fields_changed.length
                ? ` ${plural(data.fields_changed.length, 'field')} updated.`
                : ' No field values changed.')
            + (moved ? ` ${plural(moved, 'related record')} moved.` : ''),
        );
    };

    const deleteRecord = (record) => {
        if (!window.confirm(`Delete ${label.toLowerCase()} ${record.id}${record.name ? ` (${record.name})` : ''}?`)) return;

        setDeletingId(record.id);
        setError(null);

        contactsApi.deleteDuplicates(entity, [record.id])
            .then(() => {
                setDeletingId(null);
                resolve([record.id]);
                setNotice(`Deleted ${label.toLowerCase()} ${record.id}.`);
            })
            .catch((err) => {
                setDeletingId(null);
                setError(err.message || `Could not delete that ${label.toLowerCase()}.`);
            });
    };

    // Groups as they stand after this screen's merges and deletes.
    const visibleGroups = result
        ? result.groups
            .map((group) => ({
                ...group,
                records: group.records.filter((r) => resolvedIds.indexOf(r.id) === -1),
            }))
            .filter((group) => group.records.length > 1)
        : [];

    const ruleLabels = availableRules.reduce((labels, option) => {
        labels[option.id] = option.label;
        return labels;
    }, { ...RULE_LABELS });

    const skipped = result ? result.skipped_rules || {} : {};

    // All six rules are shown; the ones this list has no field for are
    // disabled with the reason on hover, so the choice reads the same on
    // every screen and a missing rule is explained rather than absent.
    const shownRules = ALL_RULES.map((id) => {
        const available = availableRules.filter((option) => option.id === id)[0];

        return available
            ? { ...available, available: true }
            : {
                id,
                label: RULE_LABELS[id],
                strength: STRENGTHS[id],
                available: false,
                reason: skipped[id] || `${lowerPlural} have no ${RULE_LABELS[id].toLowerCase()} field`,
            };
    });

    if (merging) {
        return (
            <MergeContacts
                open
                entity={entity}
                label={label}
                plural={pluralLabel}
                records={merging.records}
                onClose={() => setMerging(null)}
                onMerged={onMerged}
            />
        );
    }

    return (
        <Modal
            show={open}
            onClose={busy ? () => {} : onClose}
            className={`FindDups ${visibleGroups.length ? 'has-results' : ''}`}
        >
            <div className="FindDups_body">
                <h2 className="FindDups_title">{`Find Duplicate ${pluralLabel}`}</h2>
                <p className="FindDups_intro">
                    {`The ${lowerPlural} in your current filtered search are compared on the one rule you pick.`}
                </p>

                <form className="FindDups_options" onSubmit={submit}>
                    <fieldset className="FindDups_fieldset">
                        <legend>Match on</legend>
                        <div className="FindDups_checks">
                            {shownRules.map((option) => (
                                <label
                                    key={option.id}
                                    className={`FindDups_option ${option.strength === 'possible' ? 'is-possible' : ''}`
                                        + `${option.available ? '' : ' is-unavailable'}`}
                                    title={option.available
                                        ? (RULE_HINTS[option.id] || '')
                                        : `Not available for ${lowerPlural}: ${option.reason}`}
                                >
                                    <input
                                        type="radio"
                                        name="contact-dup-rule"
                                        checked={rule === option.id}
                                        disabled={busy || !option.available}
                                        onChange={() => chooseRule(option.id)}
                                    />
                                    <span>{option.label}</span>
                                    {option.available && option.strength === 'possible' ? (
                                        <span className="FindDups_weak" title="Matches on a name alone">possible</span>
                                    ) : null}
                                </label>
                            ))}
                        </div>
                    </fieldset>

                    <fieldset className="FindDups_fieldset FindDups_fieldset--grow">
                        <legend>Only groups mentioning</legend>
                        <input
                            type="text"
                            className="FindDups_search"
                            value={search}
                            disabled={busy}
                            placeholder="Name, email, cell, company or city"
                            aria-label="Only groups mentioning"
                            onChange={(event) => setSearch(event.target.value)}
                        />
                    </fieldset>

                    <button
                        type="submit"
                        className={`button-new positive FindDups_submit ${busy ? 'inactive' : ''}`}
                        disabled={busy}
                    >
                        {busy ? 'Searching…' : result ? 'Search again' : 'Find Duplicates'}
                    </button>
                </form>

                {error ? <Alert type="error">{error}</Alert> : null}
                {notice ? <Alert type="success">{notice}</Alert> : null}

                {result ? (
                    <div className="FindDups_results">
                        <p className="FindDups_summary">
                            {result.group_count
                                ? `Found ${plural(result.group_count, 'group')} of duplicates, among ${plural(result.scanned, 'record')} compared.`
                                  + (result.last_page > 1
                                      ? ` Showing page ${result.page.toLocaleString()} of ${result.last_page.toLocaleString()}.`
                                      : '')
                                : `No duplicates among the ${plural(result.scanned, 'record')} compared.`}
                        </p>

                        <div className="FindDups_groups">
                            {visibleGroups.map((group, index) => (
                                <div
                                    className={`FindDups_group ${group.strength === 'possible' ? 'is-possible' : ''}`}
                                    key={group.records.map((r) => r.id).join('-')}
                                >
                                    <div className="FindDups_group_head">
                                        <span className="FindDups_group_count">
                                            {`Group ${(((result.page || 1) - 1) * (result.per_page || 0) + index + 1).toLocaleString()} · ${plural(group.records.length, 'record')}`}
                                        </span>
                                        <span className="FindDups_group_match">
                                            {'Matched on ' + group.matched_on.map((id) => ruleLabels[id] || id).join(', ')}
                                        </span>
                                        <span
                                            className={`FindDups_strength ${group.strength}`}
                                            title={group.strength === 'definite'
                                                ? 'Matched on something issued to one party — an email address or a phone line'
                                                : 'Matched on a name alone, which separate people and companies do share'}
                                        >
                                            {group.strength === 'definite' ? 'duplicate' : 'possible duplicate'}
                                        </span>
                                        <button
                                            type="button"
                                            className="button-new positive FindDups_group_merge"
                                            onClick={() => setMerging(group)}
                                        >
                                            Merge
                                        </button>
                                    </div>
                                    <table className="FindDups_table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Cell</th>
                                                <th>Company</th>
                                                <th>City</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.records.map((record) => (
                                                <tr key={record.id}>
                                                    <td>
                                                        <Link to={`${arc.path.base}${route}/${record.id}`} target="_blank">
                                                            {record.id}
                                                        </Link>
                                                    </td>
                                                    <td>{record.name || ''}</td>
                                                    <td>{record.email || ''}</td>
                                                    <td>{record.cell || ''}</td>
                                                    <td>{record.company || ''}</td>
                                                    <td>{record.city || ''}</td>
                                                    <td className="FindDups_row_action">
                                                        <button
                                                            type="button"
                                                            className={`button-new text ${deletingId === record.id ? 'inactive' : ''}`}
                                                            onClick={() => deleteRecord(record)}
                                                            disabled={deletingId === record.id}
                                                            title={`Delete ${label.toLowerCase()} ${record.id}`}
                                                        >
                                                            {deletingId === record.id ? 'Deleting…' : 'Delete'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>

                        {result.last_page > 1 ? (
                            <div className="FindDups_pager">
                                <button
                                    type="button"
                                    className={`button-new text ${busy || result.page <= 1 ? 'inactive' : ''}`}
                                    onClick={() => goToPage(result.page - 1)}
                                    disabled={busy || result.page <= 1}
                                >
                                    Previous
                                </button>
                                <span className="FindDups_pager_label">
                                    {`Page ${result.page.toLocaleString()} of ${result.last_page.toLocaleString()}`}
                                </span>
                                <button
                                    type="button"
                                    className={`button-new text ${busy || result.page >= result.last_page ? 'inactive' : ''}`}
                                    onClick={() => goToPage(result.page + 1)}
                                    disabled={busy || result.page >= result.last_page}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <p className="FindDups_summary">
                        {busy
                            ? `Comparing the ${lowerPlural} in this list…`
                            : `Choose what to match on, then search. ${
                                filteredCount
                                    ? `The ${Number(filteredCount).toLocaleString()} ${lowerPlural} in this list are compared.`
                                    : `The ${lowerPlural} in this list are compared.`
                            }`}
                    </p>
                )}

                <div className="FindDups_actions">
                    <button
                        type="button"
                        className={`button-new text ${busy ? 'inactive' : ''}`}
                        onClick={onClose}
                        disabled={busy}
                    >
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
}

FindDuplicateContacts.propTypes = {
    open: PropTypes.bool.isRequired,
    // One of investors / agents / firms / vendors.
    entity: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    plural: PropTypes.string.isRequired,
    // The edit screen's route segment, for the id link.
    route: PropTypes.string.isRequired,
    // Resolves to the ids of every row the list's current filter matches.
    fetchFilteredSet: PropTypes.func.isRequired,
    // How many rows that filter matches, for the summary line.
    filteredCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onClose: PropTypes.func.isRequired,
};
